import * as ToastPrimitive from '@radix-ui/react-toast'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { create } from 'zustand'

import { cn } from './utils'

/**
 * Toasts: a tiny store (`toast.success('Saved')`) plus a `<Toaster/>` outlet
 * mounted once in the root layout. Radix handles swipe-to-dismiss, timers,
 * focus hotkey (F8) and the live region.
 */

export type ToastTone = 'info' | 'success' | 'warning' | 'error'

export interface ToastItem {
  id: string
  tone: ToastTone
  title: ReactNode
  description?: ReactNode
  /** ms; `Infinity` keeps it until dismissed. */
  duration?: number
  action?: { label: string; onClick: () => void }
}

interface ToastState {
  items: ToastItem[]
  push(item: Omit<ToastItem, 'id'> & { id?: string }): string
  dismiss(id: string): void
  clear(): void
}

let counter = 0

export const useToastStore = create<ToastState>()((set) => ({
  items: [],
  push: (item) => {
    const id = item.id ?? `t${++counter}`
    set((s) => ({ items: [...s.items.filter((t) => t.id !== id), { ...item, id }].slice(-5) }))
    return id
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
  clear: () => set({ items: [] }),
}))

type ToastArgs = Omit<ToastItem, 'id' | 'tone' | 'title'> & { id?: string }

function make(tone: ToastTone) {
  return (title: ReactNode, opts: ToastArgs = {}) => useToastStore.getState().push({ tone, title, ...opts })
}

export const toast = {
  info: make('info'),
  success: make('success'),
  warning: make('warning'),
  error: make('error'),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
}

const toneIcon: Record<ToastTone, typeof Info> = { info: Info, success: CheckCircle2, warning: AlertTriangle, error: XCircle }
const toneClass: Record<ToastTone, string> = {
  info: 'text-info',
  success: 'text-ok',
  warning: 'text-warn',
  error: 'text-danger',
}

export function Toaster() {
  const items = useToastStore((s) => s.items)
  const dismiss = useToastStore((s) => s.dismiss)
  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
      {items.map((t) => {
        const Icon = toneIcon[t.tone]
        return (
          <ToastPrimitive.Root
            key={t.id}
            duration={t.duration}
            onOpenChange={(open) => {
              if (!open) dismiss(t.id)
            }}
            className={cn(
              'group pointer-events-auto relative flex w-full items-start gap-3 card p-4 pr-10 shadow-pop',
              'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=cancel]:translate-x-0',
            )}
          >
            <Icon className={cn('mt-0.5 size-5 shrink-0', toneClass[t.tone])} aria-hidden />
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title className="text-sm font-semibold text-ink">{t.title}</ToastPrimitive.Title>
              {t.description ? <ToastPrimitive.Description className="mt-0.5 text-sm text-ink-2">{t.description}</ToastPrimitive.Description> : null}
              {t.action ? (
                <ToastPrimitive.Action altText={t.action.label} asChild>
                  <button type="button" onClick={t.action.onClick} className="mt-2 text-sm font-medium text-accent underline-offset-4 hover:underline">
                    {t.action.label}
                  </button>
                </ToastPrimitive.Action>
              ) : null}
            </div>
            <ToastPrimitive.Close className="absolute right-2 top-2 inline-flex size-8 items-center justify-center rounded-lg text-ink-3 hover:bg-paper-2 hover:text-ink focus-visible:focus-ring">
              <X className="size-4" aria-hidden />
              <span className="sr-only">Dismiss</span>
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        )
      })}
      <ToastPrimitive.Viewport className="fixed bottom-14 right-4 z-[60] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2 outline-none" />
    </ToastPrimitive.Provider>
  )
}
